/* eslint-disable max-lines-per-function */
import { useState, useEffect } from '@wordpress/element';
import { Button, Dropdown } from 'semantic-ui-react';

import { useFetch, slugify } from './Utils';
import CountryTable from './Table';
import resetIcon from './resetIcon';

const initialState = {
	status: 'idle',
	data: {},
	allSurveys: [],
	allCountries: [],
	allYears: [],
	filteredData: [],
	filteredSurveys: [],
	filteredCountries: [],
	filteredYears: [],
	survey:
		window.prcURLVars?.surveyid !== undefined
			? window.prcURLVars?.surveyid.toLowerCase()
			: 'all-survey',
	country:
		window.prcURLVars?.countryid !== undefined
			? window.prcURLVars?.countryid.toLowerCase()
			: 'all-country',
	year:
		window.prcURLVars?.yearid !== undefined
			? window.prcURLVars?.yearid
			: 'all-year',
	databaseFiltered: false,
};

function App() {
	const [dataObj, setDataObj] = useState(initialState);
	const [countryDisabled, setCountryDisabled] = useState(false);
	const [yearDisabled, setYearDisabled] = useState(false);
	const [surveyDisabled, setSurveyDisabled] = useState(false);
	const { status, data } = useFetch(
		`${window.siteURL}/wp-json/prc-api/v2/interactive?slug=international-methodology-database`
	);
	useEffect(() => {
		let mounted = true;
		let s = [];
		let c = [];
		let y = [];
		if (status === 'fetched') {
			const categories = getCategoryArrays(data);
			s = categories.surveys;
			c = categories.countries;
			y = categories.years;
		}

		// set the filtered values to the canonical data on load
		setDataObj({
			...dataObj,
			status,
			data,
			allSurveys: s,
			allCountries: c,
			allYears: y,
			filteredData: data,
			filteredSurveys: s,
			filteredCountries: c,
			filteredYears: y,
		});

		return function cleanup() {
			mounted = false;
		};
	}, [data]);

	useEffect(() => {
		if (dataObj.data.length > 0) {
			const { survey, country, year } = dataObj;
			if (survey !== 'all-survey') {
				setSurveyDisabled(true);
				setDataObj({
					...dataObj,
					databaseFiltered: true,
				});
				filterData(survey, 'survey');
			}
			if (country !== 'all-country') {
				setCountryDisabled(true);
				setDataObj({
					...dataObj,
					databaseFiltered: true,
				});
				filterData(country, 'country');
			}
			if (year !== 'all-year') {
				setYearDisabled(true);
				setDataObj({
					...dataObj,
					databaseFiltered: true,
				});
				filterData(year, 'year');
			}
		}
	}, [dataObj.data]);

	useEffect(() => {
		window.history.pushState(
			null,
			null,
			`/methods/feature/international-methodology/${dataObj.survey}/${dataObj.country}/${dataObj.year}/`
		);
	}, [dataObj.survey, dataObj.country, dataObj.year]);

	function getCategoryArrays(data) {
		// reduce the data to unique values for each category
		const sUniq = [...new Set(data.map((d) => d.survey))];
		const cUniq = [...new Set(data.map((d) => d.country))];
		const yUniq = [...new Set(data.map((d) => d.year.toString()))];
		// map the arrays to arrays of objects for the dropdowns
		const sUnsorted = sUniq.map((survey) => ({
			text: survey,
			key: slugify(survey),
		}));
		const cUnsorted = cUniq.map((country) => ({
			text: country,
			key: slugify(country),
		}));
		const yUnsorted = yUniq.map((year) => ({
			text: year,
			key: slugify(year),
		}));
		// sort the arrays. descending for years, ascending for surveys and countries
		const surveys = sUnsorted.sort((a, b) => (a.text > b.text ? 1 : -1));
		const countries = cUnsorted.sort((a, b) => (a.text > b.text ? 1 : -1));
		const years = yUnsorted.sort((a, b) => (a.text < b.text ? 1 : -1));
		return { surveys, countries, years };
	}
	const filterData = (filteredVal, filteredCategory) => {
		const { survey, country, year, filteredData } = dataObj;
		// sanity check to make sure that we're not trying to filter the null values
		if (filteredVal === 'all-survey') {
			return;
		}
		if (filteredVal === 'all-country') {
			return;
		}
		if (filteredVal === 'all-year') {
			return;
		}
		// filter through previously filtered data to drill down further
		const newFilteredData = filteredData.filter(
			(d) => slugify(d[filteredCategory]) === filteredVal
		);
		// get new category arrays for dropdowns
		const categories = getCategoryArrays(newFilteredData);
		setDataObj({
			...dataObj,
			country: filteredCategory === 'country' ? filteredVal : country,
			year: filteredCategory === 'year' ? filteredVal : year,
			survey: filteredCategory === 'survey' ? filteredVal : survey,
			filteredData: newFilteredData,
			filteredSurveys: categories.surveys,
			filteredCountries: categories.countries,
			filteredYears: categories.years,
			databaseFiltered: true,
		});
	};
	return (
		<>
			<div className="database__instructions">
				Start by selecting a <strong>survey</strong>,{' '}
				<strong>country</strong>, or <strong>year</strong>. To filter
				results further, select from another drop down.
			</div>
			<div className="database__select-controls">
				<Dropdown
					placeholder="Select Survey"
					fluid
					search
					selection
					// clearable
					value={
						dataObj.survey === 'all-survey' ? '' : dataObj.survey
					}
					disabled={surveyDisabled}
					options={dataObj.filteredSurveys.map((s) => ({
						key: s.key,
						text: s.text,
						value: s.key,
					}))}
					onChange={(e, { value }) => {
						const survey = value.length > 0 ? value : 'all-survey';
						filterData(survey, 'survey');
						setSurveyDisabled(true);
					}}
				/>
				<Dropdown
					placeholder="Select Country"
					fluid
					search
					selection
					// clearable
					disabled={countryDisabled}
					value={
						dataObj.country === 'all-country' ? '' : dataObj.country
					}
					options={dataObj.filteredCountries.map((c) => ({
						key: c.key,
						text: c.text,
						value: c.key,
					}))}
					onChange={(e, { value }) => {
						const country =
							value.length > 0 ? value : 'all-country';
						filterData(country, 'country');
						setCountryDisabled(true);
					}}
				/>
				<Dropdown
					placeholder="Select Year"
					fluid
					search
					selection
					// clearable
					value={dataObj.year === 'all-year' ? '' : dataObj.year}
					disabled={yearDisabled}
					options={dataObj.filteredYears.map((y) => ({
						key: y.key,
						text: y.text,
						value: y.key,
					}))}
					onChange={(e, { value }) => {
						const year = value.length > 0 ? value : 'all-year';
						filterData(year, 'year');
						setYearDisabled(true);
					}}
				/>
			</div>
			<div className="database__reset-button">
				{dataObj.databaseFiltered && (
					<Button
						basic
						onClick={() => {
							setDataObj({
								...dataObj,
								databaseFiltered: false,
								survey: 'all-survey',
								country: 'all-country',
								year: 'all-year',
								filteredData: dataObj.data,
								filteredSurveys: dataObj.allSurveys,
								filteredCountries: dataObj.allCountries,
								filteredYears: dataObj.allYears,
							});
							setCountryDisabled(false);
							setSurveyDisabled(false);
							setYearDisabled(false);
						}}
					>
						{resetIcon}
						<span style={{ marginLeft: '10px' }}>
							Reset Filters
						</span>
					</Button>
				)}
			</div>
			{dataObj.status === 'fetched' && (
				<div className="database__tables">
					{dataObj.data.map((d) => (
						<CountryTable
							d={d}
							selectedSurvey={dataObj.survey}
							selectedCountry={dataObj.country}
							selectedYear={dataObj.year}
						/>
					))}
				</div>
			)}
		</>
	);
}

export default App;
